/* ============================================
   QUOTE DASHBOARD — Detail panel shell residual
   Asset: dashboard-detail-shell.js

   Extracted from dashboard-main.js on 2026-05-23 as Phase B.3.a of the
   dashboard refactor. After B.2.a-e moved 5 panel cards out of
   buildDetailHtml, this ship picks up four sibling pieces that aren't
   themselves "panels" but were sitting inline at the top + middle of
   the function:

     1. buildMobileActions(q) — the mobile-only cog menu row that
        renders at the very top of every detail panel. Hidden on desktop
        via CSS; on mobile it surfaces the same per-state actions the
        desktop cog menu does (Create Quote / Resurrect / Wake / Edit
        Hold / Mark Dead / Reassign / Generate Invoice / Delete / etc.).
        Six render branches keyed on lifecycle state.

     2. buildLinkedChildBanner(q) — the "🔗 This quote is linked to PARENT"
        banner that surfaces on cross-source linked-child rows so admin
        immediately sees where fulfillment / AR/AP / commission actually
        live. Trivial; ~7 LOC of inline render.

     3. buildBalanceChainBanner(q) — the "Linked Orders" section that
        renders the breakdown (PARENT $5,798 + CHILD1 $200 = $5,998),
        one row per linked child with substitute/edit/unlink actions
        (admin-only). 2026-05-18 minimal redesign preserved verbatim.

     4. buildFinancialsCard(q) — the column-3 Financials P&L card from
        buildDetailHtml's "default" else branch. Revenue rows
        (Goods / Freight / Card Fee / Install / Taxes) → divider → cost
        rows (COGS / Freight Cost / Install Cost / Shopify Fee / Custom
        Costs) → bottom-line GP of Goods or GP of Order depending on the
        admin Full-margin toggle. ~155 LOC, the biggest of the four.

   None of these four are "panels" in the B.2.x sense — they're sibling
   chunks of buildDetailHtml's chrome + the default-branch Financials
   card. Grouped here because they're all "shell residual" per the
   B.3.a planner item.

   Loaded via <script defer> in sections/rep-quote-dashboard.liquid BEFORE
   main.js. Public API attached to window.RQD:

     buildMobileActions(q)        -> html string (always renders)
     buildLinkedChildBanner(q)    -> html string (gates on q.linkedParentRef)
     buildBalanceChainBanner(q)   -> html string (gates on q.balanceChildren.length > 0)
     buildFinancialsCard(q)       -> html string (always renders the card)

   Each function self-gates and returns '' when its conditions aren't met.

   Init contract (RQD.initDetailShell called once by main.js at boot):
     esc, money, detailRow, costRow, freightCostScrollRow,
     _qstHasContactActivity, buildRequestLogisticsButtons — function
     declarations in main, safe to pass by reference per the init-deps-
     var-hoisting-trap memo.
     getIsAdmin — closure-getter. isAdmin is a `var` in main that
     mutates after Auth.getSession() resolves (~L91). Used by the
     mobile actions admin gates + balance chain banner action buttons
     + Financials card Full-margin toggle render.
   ============================================ */
(function() {
  'use strict';
  window.RQD = window.RQD || {};

  // Injected by RQD.initDetailShell (from main.js boot)
  var esc, money, detailRow, costRow, freightCostScrollRow,
      _qstHasContactActivity, buildRequestLogisticsButtons, getIsAdmin;
  /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-08) — shared link builders (see main.js) */
  var buildLinkExternalButton, buildLinkOpenDealButton;
  /* CUSTOM_REVIEWS_BY_CLAUDE (Ryan 2026-09-09) — the review-ask cog entry (built in main.js) */
  var buildReviewAskMenuItem;

  // -------------------------------------------------------------------
  // 1. Mobile actions row (cog menu) — top of every detail panel
  // -------------------------------------------------------------------
  function buildMobileActions(q) {
    var isAdmin = getIsAdmin();
    var isPend = q.isPending || q.status === 'pending';
    var isLead = q.isLead || q.status === 'new_lead';
    var isDeadLead = !!q.isDeadLead;
    var isSample = !!q.isSample;
    var isOnHold = !!q.isOnHold;
    // Progressed-lead detection — same rule as the desktop cog menu and
    // the status pill router. Once items land or the PDF is downloaded,
    // the row should expose standard QUOTED actions, not lead-actions.
    var _hasQuoteItemsDH = (parseFloat(q.total) || 0) > 0;
    var _pdfDoneDH = !!(q.pdfDownloaded || q.chainPdfDownloaded);
    var _isFreshLeadDH = isLead && !_hasQuoteItemsDH && !_pdfDoneDH;
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-05) — A held lead that's already
       been built into a quote (items on it, or a downloaded PDF) is PROGRESSED:
       it must expose QUOTED actions (Edit Quote), not lead actions. Before this,
       putting a built quote On Hold hid Edit Quote entirely — the only cog path
       left was "Create Quote", which mints a FRESH draft and orphans the work.
       (Reassignment was incidental; the hold was the trigger.) */
    var _isProgressedDH = _hasQuoteItemsDH || _pdfDoneDH;

    var html = '<div class="rqd-detail__mobile-actions">' +
      '<button class="rqd-actions-trigger" type="button" data-menu-ref="' + esc(q.reference) + '" data-action="open-row-actions" aria-haspopup="menu" aria-expanded="false" aria-label="Actions for ' + esc(q.reference) + '" title="Actions for this deal">&#9881;</button>' +
      '<div class="rqd-actions-menu" data-menu-for="' + esc(q.reference) + '">' +
        (_isFreshLeadDH || isDeadLead || isSample || (isOnHold && !_isProgressedDH)
          ? (function() {
              /* Ryan 2026-05-21: Activity gate replaces 72h age gate —
                 same fix as the desktop cog menu. Rep must log at least
                 one Activity entry before Mark Dead appears. */
              var canMarkDead = !isDeadLead && (isSample || isOnHold || _qstHasContactActivity(q));
              /* Ryan 2026-05-16: ON HOLD branch surfaces Create Quote
                 as primary + Remove Hold as secondary so the rep can
                 skip the two-step (mirrors desktop cog menu). */
              var primaryBtn, secondaryActionBtn;
              if (isDeadLead) {
                primaryBtn = '<button type="button" class="rqd-resurrect-lead-btn" data-ref="' + esc(q.reference) + '">Resurrect Lead</button>';
                secondaryActionBtn = '';
              } else if (isOnHold) {
                primaryBtn = '<button type="button" class="rqd-build-quote-from-lead-btn" data-ref="' + esc(q.reference) + '">Create Quote</button>';
                secondaryActionBtn = '<button type="button" class="rqd-wake-lead-btn" data-ref="' + esc(q.reference) + '">⏵ Remove Hold</button>';
              } else {
                // NEW LEAD + SAMPLE — handleBuildQuoteFromLead mints a fresh draft.
                primaryBtn = '<button type="button" class="rqd-build-quote-from-lead-btn" data-ref="' + esc(q.reference) + '">Create Quote</button>';
                secondaryActionBtn = '';
              }
              var holdBtn = isOnHold
                ? '<button type="button" class="rqd-edit-hold-btn" data-ref="' + esc(q.reference) + '">⏸ Edit Hold Date</button>'
                : (!isDeadLead ? '<button type="button" class="rqd-hold-lead-btn" data-ref="' + esc(q.reference) + '">⏸ Put On Hold</button>' : '');
              /* Ryan 2026-05-16: Reassign Rep moved below Mark Dead
                 to match desktop ordering (Reassign sits directly
                 above Delete Quote there; mobile has no Delete in
                 this branch but matching the order keeps mobile
                 and desktop menus visually consistent). */
              return primaryBtn + secondaryActionBtn + holdBtn +
                (canMarkDead ? '<button type="button" class="rqd-mark-dead-btn" data-ref="' + esc(q.reference) + '">💀 Mark Dead</button>' : '') +
                /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-08) — desktop-cog parity: an
                   ON HOLD deal can be linked to the order the customer placed themselves. */
                buildLinkExternalButton(q) +
                (isAdmin ? '<button type="button" class="rqd-reassign-btn" data-ref="' + esc(q.reference) + '">Reassign Rep</button>' : '');
            })()
        : isPend
          ? (function() {
              /* Ryan 2026-05-16: Mobile PENDING cog menu mirrors
                 desktop — gains Put On Hold / Edit Hold Date. Same
                 handler routing via .rqd-hold-lead-btn / .rqd-edit-
                 hold-btn class hooks. */
              var _pendHasFuM = !!(q.followUp && q.followUp.dueAt);
              var _pendHoldBtnM = _pendHasFuM
                ? '<button type="button" class="rqd-edit-hold-btn" data-ref="' + esc(q.reference) + '">⏸ Edit Hold Date</button>'
                : '<button type="button" class="rqd-hold-lead-btn" data-ref="' + esc(q.reference) + '">⏸ Put On Hold</button>';
              return '<a href="/pages/rep-quote-workstation?ref=' + encodeURIComponent(q.reference) + '&pending=1&from=dashboard">Create Quote</a>' +
                _pendHoldBtnM +
                '<button type="button" class="rqd-delete-pending-btn" data-ref="' + esc(q.reference) + '">Delete</button>';
            })()
          : (q.lifecycleStatus === 'sold'
            ? (!isAdmin ? '<a href="/pages/rep-quote-workstation?ref=' + encodeURIComponent(q.reference) + '&preview=1&from=dashboard">View Quote</a>' : '') +
              '<button type="button" class="rqd-download-pdf-btn" data-ref="' + esc(q.reference) + '">Download PDF</button>' +
              /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-22) - never offer to invoice a $0
                 replacement. Mirrors dashboard-quote-row.js; worker refuses it too. */
              /* CUSTOM_REVIEWS_BY_CLAUDE (Ryan 2026-09-09) — desktop-cog parity: the review-ask
                 entry (dashboard-quote-row.js). Self-gates to owner / admin. */
              buildReviewAskMenuItem(q) +
              (!q.linkedParentRef && !q.isReplacement ? '<button type="button" class="rqd-generate-invoice-btn" data-ref="' + esc(q.reference) + '">' + (q.invoiced ? 'Regenerate Invoice' : 'Generate Invoice') + '</button>' : '') +
              (isAdmin ? '<a href="/pages/rep-quote-workstation?ref=' + encodeURIComponent(q.reference) + '&from=dashboard">Edit Quote</a>' : '') +
              /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-21) — desktop-cog parity: Create
                 Replacement Order. Same class hook, same delegated handler in main.js.
                 ⛔ This menu and the desktop cog (dashboard-quote-row.js) are hand-mirrored
                 and HAVE drifted before — change both or neither. */
              (isAdmin && !q.cancelled && !q.isReplacement && !q._isBalanceChild && q.source !== 'online'
                ? '<button type="button" class="rqd-create-replacement-btn" data-ref="' + esc(q.reference) + '">↩ Create Replacement Order</button>' : '') +
              (isAdmin && q.soldBasis && q.soldBasis.source === 'external' ? '<button type="button" class="rqd-unlink-external-btn" data-ref="' + esc(q.reference) + '">Unlink External Order</button>' : '') +
              (isAdmin && q.soldBasis && q.soldBasis.source === 'external' && q.lifecycleStatus === 'sold' ? '<button type="button" class="rqd-cancel-linked-order-btn" data-ref="' + esc(q.reference) + '" data-order-name="' + esc((q.soldBasis && q.soldBasis.externalOrderName) || q.reference) + '" data-amount="' + esc(String(q.total || q.quoteAmount || '0')) + '">Cancel Online Order</button>' : '') +
              /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-08) — desktop-cog parity on SOLD:
                 link a deal marked sold to the real order, or link an online order to the open
                 deal it matches. Both self-gate to nothing when they don't apply. */
              buildLinkExternalButton(q) +
              buildLinkOpenDealButton(q) +
              (q.freightStatus === 'requested' && !q.linkedParentRef ? '<button type="button" class="rqd-cancel-freight-btn" data-ref="' + esc(q.reference) + '">Cancel Freight Request</button>' : '') + (q.installationStatus === 'requested' && !q.linkedParentRef ? '<button type="button" class="rqd-cancel-install-btn" data-ref="' + esc(q.reference) + '">Cancel Installation Request</button>' : '') /* CUSTOM_REQUESTCANCEL_BY_CLAUDE (Ryan 2026-10-13) */ +
              /* Ryan 2026-05-16: Reassign Rep directly above Delete Quote. */
              (isAdmin ? '<button type="button" class="rqd-reassign-btn" data-ref="' + esc(q.reference) + '">Reassign Rep</button>' : '') +
              (isAdmin ? '<button type="button" class="rqd-delete-quote-btn" data-ref="' + esc(q.reference) + '" data-draft-id="' + esc(q.id || '') + '">Delete Quote</button>' : '') +
              buildRequestLogisticsButtons(q)
            : '<a href="/pages/rep-quote-workstation?ref=' + encodeURIComponent(q.reference) + '&from=dashboard">Edit Quote</a>' +
              '<button type="button" class="rqd-download-pdf-btn" data-ref="' + esc(q.reference) + '">Download PDF</button>' +
              (q.signedAwaitingPayment ? '<button type="button" class="rqd-reverse-signature-btn" data-ref="' + esc(q.reference) + '">Reverse signature</button>' : '') + /* CUSTOM_SIGNEDUNPAID_BY_CLAUDE (card 0a13de9f8439) */
              '<button type="button" class="rqd-copy-pay-link-btn" data-ref="' + esc(q.reference) + '">Copy payment link</button>' + /* CUSTOM_ONLINESIGN_BY_CLAUDE (Ryan 2026-10-26) */ /* ⛔ hand-mirrored with dashboard-quote-row.js */
              /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-05) — keep hold controls
                 reachable for a PROGRESSED on-hold deal now routed to this branch
                 (they previously lived only in the lead menu). Same class hooks,
                 already event-delegated, so no new wiring. */
              (isOnHold ? '<button type="button" class="rqd-wake-lead-btn" data-ref="' + esc(q.reference) + '">⏵ Remove Hold</button>' +
                          '<button type="button" class="rqd-edit-hold-btn" data-ref="' + esc(q.reference) + '">⏸ Edit Hold Date</button>' : '') +
              buildRequestLogisticsButtons(q) +
              /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-08) — was the lone inline copy of
                 this action; now the shared self-gating builder (see main.js). The open-deal
                 twin rides along: an online order with no lifecycle record misses the sold
                 branch above and lands here. */
              buildLinkExternalButton(q) +
              buildLinkOpenDealButton(q) +
              /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-03) — "Link to inquiry" (mobile/detail
                 cog twin); self-hides unless the customer has an OPEN inquiry (q.hasOpenInquiry). */
              (isAdmin && q.hasOpenInquiry ? '<button type="button" class="rqd-link-inquiry-btn" data-ref="' + esc(q.reference) + '" data-lead-ref="' + esc(q.openInquiryRef || '') + '">🔗 Link to inquiry</button>' : '') +
              (q.freightStatus === 'requested' && !q.linkedParentRef ? '<button type="button" class="rqd-cancel-freight-btn" data-ref="' + esc(q.reference) + '">Cancel Freight Request</button>' : '') + (q.installationStatus === 'requested' && !q.linkedParentRef ? '<button type="button" class="rqd-cancel-install-btn" data-ref="' + esc(q.reference) + '">Cancel Installation Request</button>' : '') /* CUSTOM_REQUESTCANCEL_BY_CLAUDE (Ryan 2026-10-13) */ +
              /* Ryan 2026-05-16: Reassign Rep directly above Delete Quote. */
              (isAdmin ? '<button type="button" class="rqd-reassign-btn" data-ref="' + esc(q.reference) + '">Reassign Rep</button>' : '') +
              (isAdmin ? '<button type="button" class="rqd-delete-quote-btn" data-ref="' + esc(q.reference) + '" data-draft-id="' + esc(q.id || '') + '">Delete Quote</button>' : '')
          )
        ) +
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-24) — Deal grouping ("Same
           Deal" link), mobile menu (mirrors desktop; same class hooks → shared
           delegated handler). */
        ((q.cancelled || q._isBalanceChild) ? '' :
          (q.isDealGroup
            ? '<button type="button" class="rqd-link-deal-btn" data-ref="' + esc(q.reference) + '">🔗 Link Another Deal</button>' +
              '<button type="button" class="rqd-ungroup-deal-btn" data-ref="' + esc(q.reference) + '">🔗 Ungroup This Deal</button>' +
              (q.linkGroupIsPrimaryBase ? '' : '<button type="button" class="rqd-set-primary-deal-btn" data-ref="' + esc(q.reference) + '">⭐ Make Group Primary</button>')
            : '<button type="button" class="rqd-link-deal-btn" data-ref="' + esc(q.reference) + '">🔗 Link as Same Deal</button>')) +
      '</div>' +
    '</div>';
    return html;
  }

  // -------------------------------------------------------------------
  // 2. Linked-child banner — "🔗 This quote is linked to PARENT"
  // -------------------------------------------------------------------
  function buildLinkedChildBanner(q) {
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — Linked-child banner. When the row is
       a cross-source linked child (q.linkedParentRef set), give admin a
       clear in-panel cue that this is part of a bigger deal. Click the
       parent ref to jump to it. Sits at the top of the detail panel,
       above all the cards, so it's the first thing the admin sees on
       expand. */
    if (!q.linkedParentRef) {
      /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-08) — same banner slot, the external-link
         relationship. The customer was worked offline on that deal and then checked out on the
         site; the deal owns the story (notes, activity, lead source, ad attribution, commission)
         and this order is just where the money landed. Reuses the existing jump-to-parent link
         so the deal is one click away. Two states below it: matched-but-not-yet-linked gets a
         prompt instead, pointing at the cog. */
      if (q.externalLinkedToRef) {
        return '<div class="rqd-detail__linked-banner">' +
          '<span class="rqd-detail__linked-banner-icon">🔗</span> ' +
          'This order is the payment for <a class="rqd-detail__linked-banner-ref" href="#" data-jump-to-parent="' + esc(q.externalLinkedToRef) + '">' + esc(q.externalLinkedToRef) + '</a> — that deal holds the notes, lead source, and commission.' +
        '</div>';
      }
      if (q.openDealMatchRef) {
        return '<div class="rqd-detail__linked-banner">' +
          '<span class="rqd-detail__linked-banner-icon">&#9889;</span> ' +
          'This customer has an open deal, <a class="rqd-detail__linked-banner-ref" href="#" data-jump-to-parent="' + esc(q.openDealMatchRef) + '">' + esc(q.openDealMatchRef) + '</a>' +
          ' (same ' + (q.openDealMatchOn === 'company' ? 'company' : 'email') + '). If this is that sale, link it from the cog menu so it lands on the deal you worked.' +
        '</div>';
      }
      return '';
    }
    return '<div class="rqd-detail__linked-banner">' +
      '<span class="rqd-detail__linked-banner-icon">🔗</span> ' +
      'This quote is linked to <a class="rqd-detail__linked-banner-ref" href="#" data-jump-to-parent="' + esc(q.linkedParentRef) + '">' + esc(q.linkedParentRef) + '</a> — fulfillment, AR/AP, and commission live on the parent.' +
    '</div>';
  }

  // -------------------------------------------------------------------
  // 3. Balance chain banner — "Linked Orders" minimal layout
  // -------------------------------------------------------------------
  function buildBalanceChainBanner(q) {
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — Balance chain banner. When this online
       row has children linked via /balance-link, render a compact summary
       at the top of the detail panel: each child's ref, reason, amount,
       and (if a swap is configured) a "Original SKU → Fulfilled SKU" line.
       Includes an Unlink button per child. */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-18, redesign) — Balance
       Chain section condensed to a clean minimal layout. Section
       title only; one row per linked child showing REF · amount ·
       status badges · actions. Substitution sub-banner dropped (the
       Order Contents card combined-view already conveys the same
       info). Swap-detail + swap-note sub-lines dropped (Order
       Contents now shows the substituted item directly). Pill
       (Swap/Balance) dropped (the substitutesParent toggle state +
       active visual conveys this). Combined-total subline now
       renders as the explicit breakdown Ryan asked for. Functionality
       preserved — same data-* attrs feed the same click handlers. */
    if (!(Array.isArray(q.balanceChildren) && q.balanceChildren.length > 0)) return '';

    var isAdmin = getIsAdmin();
    var html = '';
    var _balancesByRef = {};
    (q.balances || []).forEach(function (b) { if (b && b.ref) _balancesByRef[b.ref] = b; });
    html += '<div class="rqd-linked">';
    html += '<div class="rqd-linked__title">Linked Orders</div>';
    // Breakdown: SHOP-1699 ($5,798) + SHOP-1700 ($200) = $5,998
    var _parentBase = parseFloat(q.totalBeforeBalances || 0) || 0;
    var _comboParts = [esc(q.reference) + ' ' + money(_parentBase)];
    q.balanceChildren.forEach(function (c) {
      _comboParts.push(esc(c.orderName || c.ref) + ' ' + money(parseFloat(c.amount) || 0));
    });
    var _comboTotal = money(parseFloat(q.total) || 0);
    html += '<div class="rqd-linked__breakdown">' + _comboParts.join(' <span class="rqd-linked__plus">+</span> ') + ' <span class="rqd-linked__eq">=</span> <strong>' + _comboTotal + '</strong></div>';
    // Row per linked child
    q.balanceChildren.forEach(function (c) {
      var meta = _balancesByRef[c.ref] || {};
      var _isSubstituting = !!meta.substitutesParent;
      html += '<div class="rqd-linked__row' + (_isSubstituting ? ' rqd-linked__row--sub' : '') + '">';
      html += '<span class="rqd-linked__ref">' + esc(c.orderName || c.ref) + '</span>';
      html += '<span class="rqd-linked__amt">' + money(parseFloat(c.amount) || 0) + '</span>';
      if (meta.collectedAt) html += '<span class="rqd-linked__badge rqd-linked__badge--paid" title="Customer payment received">Paid</span>';
      if (_isSubstituting) html += '<span class="rqd-linked__badge rqd-linked__badge--sub" title="This child\'s product is being used in place of the parent\'s original line">Substituting</span>';
      if (isAdmin) {
        html += '<div class="rqd-linked__actions">';
        // Toggle substitution
        html += '<button type="button" class="rqd-balance-substitute-btn rqd-linked__btn"'
          + ' data-ref="' + esc(q.reference) + '"'
          + ' data-child-ref="' + esc(c.ref) + '"'
          + ' data-substitutes="' + (_isSubstituting ? '1' : '0') + '"'
          + ' title="' + (_isSubstituting ? 'Revert to parent\'s items' : 'Use ' + esc(c.ref) + '\'s product in place of parent\'s') + '">'
          + (_isSubstituting ? 'Revert' : 'Substitute')
          + '</button>';
        // Edit link
        html += '<button type="button" class="rqd-edit-balance-btn rqd-linked__btn"'
          + ' data-ref="' + esc(q.reference) + '"'
          + ' data-child-ref="' + esc(c.ref) + '"'
          + ' title="Edit this link">Edit</button>';
        // Unlink
        html += '<button type="button" class="rqd-unlink-balance-btn rqd-linked__btn rqd-linked__btn--danger"'
          + ' data-ref="' + esc(q.reference) + '"'
          + ' data-child-ref="' + esc(c.ref) + '"'
          + ' title="Unlink this order">Unlink</button>';
        html += '</div>';
      }
      html += '</div>';
      if (meta.swapNote) {
        html += '<div class="rqd-linked__note">' + esc(meta.swapNote) + '</div>';
      }
    });
    html += '</div>';
    return html;
  }

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-21 PM²) — the pallet-fee ESTIMATE, for deals that
     have no PO yet.

     Ryan, on RW-1284-A: *"now that I've set the $60 pallet fee for Legend Fitness, should that $60
     not be reflected in the financials?"* It wasn't, because the fee is only resolved when a PO is
     generated — so every un-purchased Legend deal read $60 a machine too high for exactly as long
     as the margin decision was still open.

     ⛔ THE ESTIMATE NEEDS REAL LINE ITEMS AND SAYS NOTHING WITHOUT THEM. Quantities live on the
     Shopify draft, not in KV: the workstation item map is per-product metadata keyed by both
     variant GID and title (one product, two entries) with no qty at all. On an offline deal the
     lines only arrive with the lazy /quote-details fetch, so this returns null until then — a
     blank row is honest, a guessed count is not.

     `isStackUpgrade` is carried on the line by the worker rather than sniffed from a title here;
     an upgrade rides its machine's pallet, and re-deriving that question in the theme is how two
     surfaces end up disagreeing about the same line. */
  function _vendorLooseEq(a, b) {
    var x = String(a || '').toLowerCase().trim();
    var y = String(b || '').toLowerCase().trim();
    if (!x || !y) return false;
    return x === y || x.indexOf(y + ' ') === 0 || y.indexOf(x + ' ') === 0;
  }
  function estimatePalletFee(q) {
    var rates = q && q.vendorPalletFees;
    if (!rates) return null;
    var lines = (q.offlineLineItems && q.offlineLineItems.length) ? q.offlineLineItems
      : ((q.onlineLineItems && q.onlineLineItems.length) ? q.onlineLineItems : null);
    if (!lines) return null;
    var models = q.vendorShippingModels || {};
    var overrides = q.freightOverrides || {};
    var total = 0, parts = [];
    Object.keys(rates).forEach(function (v) {
      var rate = parseFloat(rates[v]) || 0;
      if (rate <= 0) return;
      /* Mirrors the worker's bsfsCarrier test with what the card has: an explicit our-carrier
         override says yes outright; otherwise any shipping model where the SUPPLIER moves the
         goods says no, and 'standard' means we broker it. */
      var o = overrides[v];
      var brokered;
      if (o && o.mode === 'our-carrier') {
        brokered = true;
      } else {
        var m = models[v] || 'standard';
        brokered = (m !== 'parcel-only' && m !== 'freight-in-product' && m !== 'flat-rate');
      }
      if (!brokered) return;
      var count = 0;
      lines.forEach(function (li) {
        if (!li || li.isStackUpgrade) return;
        if (!_vendorLooseEq(li.vendor, v)) return;
        var qy = parseInt(li.effectiveQuantity != null ? li.effectiveQuantity : li.quantity, 10);
        if (qy > 0) count += qy;
      });
      if (count > 0) { total += rate * count; parts.push({ vendor: v, rate: rate, count: count }); }
    });
    if (!(total > 0)) return null;
    return { amount: Math.round(total * 100) / 100, parts: parts };
  }

  // -------------------------------------------------------------------
  // 4. Financials default-branch card (column 3 of detail grid)
  // -------------------------------------------------------------------
  function buildFinancialsCard(q, opts) {
    var isAdmin = getIsAdmin();
    var fin = q.financials || {};
    var html = '';
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-22) — our-carrier freight override
       presence. When set, the deal carries a real freight cost we're paying (folded
       into financials.freightCost), so the Financials card defaults to Full-margin —
       the GP-of-Order math subtracts freight and the Freight Cost row + ✎ render.
       Without this, the rep-friendly GP-of-Goods view (freight excluded by design)
       hid the cost and the bottom line read too high. _ourCarrierVendor (the single
       override vendor) drives the inline cost-edit ✎ below. Commission is unaffected
       (server-side gpOfGoods, freight-excluded). */
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-08 PM⁸) — dropped the `q.source !== 'online'`
       gate here too. Now that an online order can carry an our-carrier override (see the switch
       in dashboard-po-lens.js), its Financials card has to show the freight cost and its ✎ —
       otherwise the money is being spent somewhere the admin can't see or correct, which is how
       an override quietly goes stale. */
    var _ocKeys = q.freightOverrides
      ? Object.keys(q.freightOverrides).filter(function (k) { var o = q.freightOverrides[k]; return o && o.mode === 'our-carrier'; })
      : [];
    var _hasOurCarrierFreight = _ocKeys.length > 0;
    var _ourCarrierVendor = _ocKeys.length === 1 ? _ocKeys[0] : null;
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-21) — the supplier's pallet / packaging fee. Same
       argument as the our-carrier freight above, and the same remedy: it is real money out on a
       shipment we broker, so a card that hides it reads too high. Its presence defaults this card
       to Full margin so the number is on screen where it can be corrected. */
    var _palletFee = parseFloat(fin.palletFee || 0);
    /* No PO has stamped a fee yet — fall back to the estimate so an un-purchased deal stops
       reading high. The stored value always wins: once the PO exists it is the authoritative
       number, and a count corrected by hand must never be talked over by a re-derivation. */
    var _palletEst = _palletFee > 0 ? null : estimatePalletFee(q);
    if (_palletEst) _palletFee = _palletEst.amount;
    var _hasPalletFee = _palletFee > 0;
    /* The vendor+bucket slot this fee is stored under, so the ✎ can post to /pallet-fee-set. Only
       offered when ONE slot is in play — with two, "which one does the pencil edit?" has no
       non-arbitrary answer, and each bucket's fee is editable from its own panel. */
    var _palletKeys = q.palletFees ? Object.keys(q.palletFees) : [];
    var _palletOne = _palletKeys.length === 1 ? q.palletFees[_palletKeys[0]] : null;
    /* CUSTOM_NOCHARGEADD_BY_CLAUDE (Ryan 2026-09-12) — goods Adjust Order put on the customer's
       PAID order that we did not charge for (the No charge tick). Real money out on a deal whose
       total never moved, so without this the card reads too high by exactly what we gave away.
       ⚠️ ABSENT on any row baked before 2026-09-12 — `|| 0` is the bridge, and it means an old row
       reads exactly as it did yesterday rather than as NaN. `giveaways` names the items; an
       unexplained dip in profit is the number somebody loses an afternoon to. */
    var _giveawayCost = parseFloat(fin.giveawayCost || 0) || 0;
    var _giveaways = Array.isArray(fin.giveaways) ? fin.giveaways : [];

    html += '<div class="rqd-detail__card">';
    // Admin-only "Full margin" toggle on offline orders. Default off →
    // shows the rep-friendly view (Freight / Install costs default to
    // their charged values, so they cancel out and the bottom line is
    // GP of Goods = directTotal − COGS, exactly the existing total). On
    // → reveals the real freight + install costs and recomputes the
    // bottom line as GP of Order (full post-cost net). Online orders
    // always show full margin since admin sees everything anyway.
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-09-06) — a freight or install LOSS defaults the
       card to Full margin too. RW-1196-A: Go Rocket's rate rose to $3,700 after the sale against
       $2,490.68 charged; Ryan edited the cost in the Workstation, came back to this card and saw
       nothing move — because the rep-friendly view prints freight cost AS the charged amount, so
       the two cancel by construction and a $1,209 loss is invisible until the toggle is ticked.
       Same reasoning the our-carrier and pallet-fee defaults above already use: real money out
       must be on screen where it can be seen and corrected. A freight MARKUP does not trigger it —
       that view is GP of Goods on purpose, and a profit hidden is not a number that reads wrong.
       ⛔ Commission is unaffected: payouts stay on GP of Goods (Option A, 2026-09-06). */
    var _hasAddOnLoss = (parseFloat(fin.freightCost || 0) > parseFloat(fin.freightCharged || 0) + 0.005)
      || (parseFloat(fin.installCost || 0) > parseFloat(fin.installCharged || 0) + 0.005);
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (AIOPS 597c8b1b4638, Ryan 2026-09-10) — ⛔ `isAdmin` NOW GATES
       ALL FIVE, AND THE ONLINE ARM IS THE ONE THAT CHANGED.
       ─────────────────────────────────────────────────────────────────────────────
       The comment above ends "Online orders always show full margin since admin sees everything
       anyway" — an assumption about WHO OPENS an online deal, written when this card was thought
       of as an admin surface, and never true. `q.source === 'online'` sat outside every admin
       check, so on a SHOP- deal a sales rep's Financials card printed `fin.freightCost` and
       `fin.installCost` raw: the real money paid to the carrier and the installer, on the one
       source of deal that arrives without anybody deciding to show it to them.
       Ryan, 2026-09-10: *"reps must not see freight OR install costs; only what we charge."*

       ⚠️ AN ADMIN LOSES NOTHING. Online still defaults to full margin for them — it is the first
       disjunct — and the four offline triggers are unchanged. What a rep now gets on an online
       deal is the same rep-friendly view they already got on every offline one: costs default to
       their charged values and cancel, so the bottom line is GP of Goods.
       ⚠️ AND THE TOGGLE IS STILL HIDDEN ON ONLINE (`_finToggleHtml` below), so this is the only
       thing deciding the online case; there is no second path back to the raw numbers. */
    var _isFullMargin = isAdmin && (
      q.source === 'online'
      || _hasOurCarrierFreight
      || _hasPalletFee
      /* CUSTOM_NOCHARGEADD_BY_CLAUDE (Ryan 2026-09-12) — a give-away joins the pallet fee's list
         for the pallet fee's reason: it is money out that the rep view cannot show, so the deal
         opens on the view where the number is visible. Without this an admin would see GP of Goods
         — deliberately unmoved by a give-away — and conclude the cost never landed. */
      || _giveawayCost > 0
      || _hasAddOnLoss
      || (function () {
        try { return localStorage.getItem('dashboard_fin_full_margin') === 'true'; } catch (e) { return false; }
      })()
    );
    var _finToggleHtml = (isAdmin && q.source !== 'online')
      ? '<label class="rqd-fin-toggle" title="Reveal real freight + install costs and recompute as GP of Order"><input type="checkbox" data-fin-toggle="full-margin" data-ref="' + esc(q.reference) + '"' + (_isFullMargin ? ' checked' : '') + '> Full margin</label>'
      : '';
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-19) — pencil to edit deal terms
       (QW Terms & Options) without opening the Workstation. Shown on QUOTED +
       in-progress ('...') pre-sale offline deals — the caller passes
       opts.canEditTerms (statusClass === 'quoted'), since those rows often have
       a null q.lifecycleStatus. Pending deals get the same pencil on their
       Financials placeholder (dashboard-main.js). Excluded on sold/lost/online. */
    var _termsPencil = (opts && opts.canEditTerms)
      ? '<button type="button" class="rqd-edit-terms-pencil" data-edit-terms-ref="' + esc(q.reference) + '" title="Edit deal terms" aria-label="Edit deal terms" style="background:none;border:none;cursor:pointer;padding:2px 6px;margin-left:6px;color:var(--rq-text-secondary);font-size:13px;vertical-align:middle;line-height:1;">&#9998;</button>'
      : '';
    html += '<div class="rqd-detail__section-title">Financials' + _termsPencil + _finToggleHtml + '</div>';
    // Revenue first (Goods → Freight → Install → Taxes), then a divider,
    // then costs (red + leading minus). Reads top-to-bottom like a P&L.
    var _freightCharged = parseFloat(fin.freightCharged || 0);
    var _freightCostRaw = parseFloat(fin.freightCost || 0);
    var _installCostRaw = parseFloat(fin.installCost || 0);
    var _installCharged = parseFloat(fin.installCharged || 0);
    // Cost defaulting:
    //   - Full margin OFF (rep-friendly): freight cost = freight charged
    //     + install cost = install charged → both cancel, bottom line is
    //     GP of Goods (directTotal − COGS).
    //   - Full margin ON (admin truth): use the raw stored values even
    //     if 0 — admin sees real numbers regardless of what's been
    //     recorded, so the difference vs the rep view is obvious.
    var _freightCost = _isFullMargin ? _freightCostRaw : _freightCharged;
    var _installCost = _isFullMargin ? _installCostRaw : _installCharged;
    var _cogs = parseFloat(fin.costTotal || 0);
    var _direct = parseFloat(fin.directTotal || 0);
    var _shopifyFees = parseFloat(q.shopifyFees || fin.shopifyFees || 0);
    var _cardFee = parseFloat(fin.cardFee || 0);
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-26) — a Fees & Extras discount, always <= 0.
       Shipped by the worker's buildQuoteSummary, which derives it from the deal's own fee rows
       rather than trusting the saved snapshot, so a deal discounted before 2026-08-26 reports
       correctly here without anyone re-saving it. `Math.min(0, …)` because this expression is only
       ever the discount half — a positive custom fee is a pass-through and moves no GP figure. */
    var _feeDiscounts = Math.min(0, parseFloat(fin.feeDiscounts || 0) || 0);
    var _taxes = parseFloat(fin.taxes || 0);

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-17) — Card Fee is a REVENUE row.
       It used to render below the divider via costRow(), which hard-prefixes a
       "−" and paints it red — while the GP formula below ADDS it (the canonical
       treatment fixed 2026-05-14: the customer-paid surcharge is revenue, and
       Shopify's processing fee is the matching cost). So the card showed
       −$62.38 for a number the maths counted as +$62.38, and the revenue block
       could never sum to the deal's Amount.
       Driving case SHOP-1933: Goods $3,384.65 + Freight $687.90 = $4,072.55
       against an Amount of $4,134.93 — the $62.38 gap was the card fee sitting
       on the wrong side of the divider. Revenue now reconciles to Amount. */
    // Revenue rows
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-22) — ⛔ A DIVIDER WITH NOTHING ABOVE IT DIVIDES
       NOTHING. Every revenue row here is gated on `> 0`, so on a deal with no revenue at all —
       a REPLACEMENT, which is $0 to the customer by definition — none of them render and this
       rule landed flush under the section title's own 2px underline. Two horizontal lines
       stacked, separating the header from the first cost row. Ryan: "that line sits right below
       the main header underline — meaning we essentially have 2 dividing lines there."
       Counted rather than guessed at, so the rule appears exactly when it has a job to do. */
    var _revenueRows = 0;
    if (_direct > 0) { html += detailRow('Goods', money(_direct)); _revenueRows++; }
    if (_freightCharged > 0) { html += detailRow('Freight', money(_freightCharged)); _revenueRows++; }
    if (_cardFee > 0 && (q.source !== 'online' || (parseFloat(q._balanceCardFeeAllocation) || 0) > 0)) { html += detailRow('Card Fee', money(_cardFee)); _revenueRows++; }
    if (q.source !== 'online' && _installCharged > 0) { html += detailRow('Install', money(_installCharged)); _revenueRows++; }
    if (_taxes > 0) { html += detailRow('Taxes', money(_taxes)); _revenueRows++; }
    if (_revenueRows > 0) html += '<div class="rqd-detail__divider"></div>';
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

    // Cost rows — red text + leading minus. In full-margin mode (admin
    // toggle on, or any online order) keep Freight Cost / Install Cost
    // visible even at $0 so the admin can see the real value the GP
    // calc is using and compare it to the charged side.
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-19) — COGS is now a
       read-only computed value: Σ(effective per-line cost × qty) +
       customCosts. Granular editing happens at the per-line cost ✎
       (Order Contents card) and the per-component cost ✎ (Order
       Contents bundle sub-rows). Custom Costs row handles one-off
       supplier-paid extras outside line items. */
    html += costRow('COGS', _cogs);
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    // CUSTOM_RFQPORTAL_BY_CLAUDE — Show the Freight Cost row when there's
    // a freight value to display OR when there's an unresolved parcel-
    // rate-pending bucket (so admin sees the ✎ even when freightCost
    // is still $0 — that's literally the case that needs the click).
    var _finHasBuckets = isAdmin && Array.isArray(q.shippingBuckets) && q.shippingBuckets.length > 0;
    var _finHasPendingParcel = _finHasBuckets && q.shippingBuckets.some(function (b) {
      return b && b.parcelRatePending && b.parcelRatePending.since;
    });
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-22) — when this offline deal has a
       single "our own carrier" freight override (_ourCarrierVendor, computed at the
       top of this fn), the Freight Cost row gets a ✎ to edit our freight cost inline
       (opens the same popover as the Purchasing-card switch; data-freight-override-
       action is delegated in dashboard-main.js). */
    if (_freightCost > 0 || (_isFullMargin && _freightCharged > 0) || _finHasPendingParcel) {
      if (_ourCarrierVendor) {
        var _fcPencil = '<button type="button" class="rqd-freight-override-edit" data-freight-override-action="edit-cost" data-ref="' + esc(q.reference) + '" data-vendor="' + esc(_ourCarrierVendor) + '" title="Edit our freight cost (Blue Sky own carrier)" aria-label="Edit our freight cost" style="background:none;border:none;cursor:pointer;padding:0 4px;color:var(--rq-text-secondary);font-size:12px;line-height:1;vertical-align:middle;">&#9998;</button>';
        html += '<div class="rqd-detail__row rqd-detail__row--cost"><span>Freight Cost <span style="color:var(--rq-text-muted);font-size:11px;">(our carrier)</span>' + _fcPencil + '</span><span>&minus;' + money(_freightCost) + '</span></div>';
      } else {
        html += _finHasBuckets
          ? freightCostScrollRow(q.reference, _freightCost)
          : costRow('Freight Cost', _freightCost);
      }
    }
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-21) — Pallet / Packaging, directly under Freight
       Cost because that is what it is: a freight-side cost, deliberately outside COGS so product
       margin and rep commission never move with it.

       Only rendered in the full-margin view, which is the GP-of-Order view. The rep view is
       GP of Goods by construction — freight and install cancel there — and a pallet fee shown
       against a bottom line that doesn't subtract it would just be a number that doesn't add up.

       The ✎ posts to /pallet-fee-set, where a human edit outranks the automatic count for good.
       It shows the count so "1 x $60" is legible at a glance: getting the COUNT wrong (an exempt
       bar) is the failure mode, not getting the rate wrong. */
    if (_isFullMargin && _palletFee > 0) {
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-21 PM³) — the row is just "Pallet". Ryan asked for
         the label and its parenthetical gone: this sits in a column of one-or-two-word cost rows
         (COGS, Freight Cost, Shopify Fee) and a trailing "(est. 1 x $60.00 — confirmed on the PO)"
         was the longest thing in the card by some way. The breakdown is still reachable — the
         pencil's popover shows the live count x rate, and the PO prints the line itself.

         An estimate still carries NO pencil: there is no stored record to edit yet, and
         /pallet-fee-set writes a count against a rate the record does not hold. It becomes
         editable the moment the PO stamps it, which is also when it stops being a guess. */
      var _pfPencil = (isAdmin && _palletOne && !_palletEst)
        ? '<button type="button" class="rqd-pallet-fee-edit" data-pallet-fee-action="edit" data-ref="' + esc(q.reference) + '" data-vendor="' + esc(_palletOne.vendor || _palletKeys[0]) + '" data-bucket="' + esc(_palletOne.bucket || '') + '" data-count="' + esc(String(_palletOne.count != null ? _palletOne.count : '')) + '" title="Edit the pallet / packaging fee" aria-label="Edit the pallet fee" style="background:none;border:none;cursor:pointer;padding:0 4px;color:var(--rq-text-secondary);font-size:12px;line-height:1;vertical-align:middle;">&#9998;</button>'
        : '';
      html += '<div class="rqd-detail__row rqd-detail__row--cost"><span>Pallet' + _pfPencil + '</span><span>&minus;' + money(_palletFee) + '</span></div>';
    }
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    /* CUSTOM_NOCHARGEADD_BY_CLAUDE (Ryan 2026-09-12) — what we gave away, named. It rides in the
       same column as Pallet and for the same reason: real money out that the order total does not
       show. The title attribute lists the items, because "Given at no charge  −$17.82" answers
       what but not what-of, and this row exists to stop a quiet dip in profit reading as a bug. */
    if (_isFullMargin && _giveawayCost > 0) {
      var _gaTitle = _giveaways.length
        ? _giveaways.map(function (g) { return (g.quantity || 1) + ' × ' + (g.title || 'item'); }).join(', ')
        : 'Added to the order at no charge';
      html += '<div class="rqd-detail__row rqd-detail__row--cost" title="' + esc(_gaTitle) + '">'
        + '<span>No Charge</span><span>&minus;' + money(_giveawayCost) + '</span></div>';
    }
    if (q.source !== 'online' && (_installCost > 0 || (_isFullMargin && _installCharged > 0))) html += costRow('Install Cost', _installCost);
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — Card Fee moved ABOVE the divider (revenue) on
       2026-08-17; see the note at the revenue rows. The visibility gate travelled
       with it unchanged: normally hidden on online orders, but shown when an
       auto-reconcile routed a balance child's card surcharge into the Card Fee
       line (otherwise that $$ would be invisible). Shopify Fee stays here — that
       one IS Blue Sky's real expense, and the two net out. */
    if (_shopifyFees > 0) html += costRow('Shopify Fee', _shopifyFees);

    /* CUSTOM_RFQPORTAL_BY_CLAUDE — Custom Costs (admin-only, online).
       One-off supplier-paid costs (state tax we can't avoid without
       a reseller cert, drop-ship fees, etc.) render as additional
       cost rows here alongside COGS / Freight / Shopify Fee.
       Each row shows label + vendor + 📄 (when addToPo:true) + ✕.
       A collapsed "+ Add Custom Cost" row expands inline into a small
       form (label / amount / vendor / Show on PO / Save).
       Click delegation handlers below match by data-cc-action. The
       sum already feeds q.financials.costTotal via the dashboard
       worker, so adding/removing here moves GP correctly. */
    if (q.source === 'online' && isAdmin) {
      var customCosts = Array.isArray(q.customCosts) ? q.customCosts : [];
      var orderVendors = (fin && Array.isArray(fin.vendors)) ? fin.vendors : [];
      customCosts.forEach(function (cc) {
        /* CUSTOM_RFQPORTAL_BY_CLAUDE — auto-reconciled GOODS (balanceGood) are shown as
           a line on the Order Contents card and their cost is already summed into the
           COGS total, so they should NOT also appear as a standalone cost row here. */
        if (cc.balanceGood) return;
        var amt = parseFloat(cc.amount) || 0;
        var vendorBadge = cc.vendorTag ? ' <span style="color:var(--rq-text-muted);font-size:11px;">(' + esc(cc.vendorTag) + ')</span>' : '';
        var poBadge = cc.addToPo ? ' <span title="Appears on the supplier PO" style="opacity:0.7;">📄</span>' : '';
        var removeBtn = '<button type="button" class="rqd-cc-remove" data-cc-action="delete" data-cc-id="' + esc(cc.id) + '" data-ref="' + esc(q.reference) + '" title="Remove this custom cost" style="background:none;border:none;color:#dc2626;cursor:pointer;font-size:13px;line-height:1;padding:0 4px;margin-left:6px;opacity:0.6;">✕</button>';
        html += '<div class="rqd-detail__row rqd-detail__row--cost rqd-cc-saved-row" data-cc-id="' + esc(cc.id) + '" data-ref="' + esc(q.reference) + '">';
        html += '<span>' + esc(cc.label || 'Custom Cost') + vendorBadge + poBadge + removeBtn + '</span>';
        html += '<span>−' + money(amt) + '</span>';
        html += '</div>';
      });
      // Collapsed add affordance + inline form (toggled by data-cc-action="add-trigger")
      var vendorOpts = '<option value="">— Any vendor —</option>' + orderVendors.map(function (v) {
        return '<option value="' + esc(v) + '">' + esc(v) + '</option>';
      }).join('');
      // Default-select the first vendor when the order has exactly one (most common)
      var defaultVendor = orderVendors.length === 1 ? orderVendors[0] : '';
      var defaultVendorOpts = orderVendors.length === 1
        ? '<option value="">— Any vendor —</option>' + orderVendors.map(function (v) {
            return '<option value="' + esc(v) + '"' + (v === defaultVendor ? ' selected' : '') + '>' + esc(v) + '</option>';
          }).join('')
        : vendorOpts;
      html += '<div class="rqd-cc-add-trigger-row" data-ref="' + esc(q.reference) + '" style="padding:4px 0;">';
      html += '<button type="button" class="rqd-cc-add-trigger" data-cc-action="add-trigger" data-ref="' + esc(q.reference) + '" style="background:none;border:1px dashed var(--rq-border,#d1d5db);border-radius:4px;padding:3px 10px;font-size:11px;color:var(--rq-text-muted);cursor:pointer;font-family:inherit;width:100%;text-align:left;">+ Add Custom Cost</button>';
      html += '</div>';
      html += '<div class="rqd-cc-add-form" data-ref="' + esc(q.reference) + '" style="display:none;padding:6px 0;border-top:1px dashed var(--rq-border,#e5e7eb);border-bottom:1px dashed var(--rq-border,#e5e7eb);margin:4px 0;">';
      html += '<div style="display:grid;grid-template-columns:1fr 80px 130px;gap:6px;align-items:center;margin-bottom:6px;">';
      html += '<input type="text" class="rqd-cc-new-label" placeholder="Label (e.g. CA tax)" maxlength="120" style="padding:4px 6px;border-radius:4px;font-family:inherit;font-size:12px;">';
      html += '<input type="number" class="rqd-cc-new-amount" placeholder="0.00" min="0" step="0.01" style="padding:4px 6px;border-radius:4px;font-family:inherit;font-size:12px;text-align:right;">';
      html += '<select class="rqd-cc-new-vendor" style="padding:4px 6px;border-radius:4px;font-family:inherit;font-size:12px;">' + defaultVendorOpts + '</select>';
      html += '</div>';
      html += '<div style="display:flex;justify-content:space-between;align-items:center;font-size:11px;">';
      html += '<label style="display:flex;align-items:center;gap:4px;cursor:pointer;color:var(--rq-text-muted);"><input type="checkbox" class="rqd-cc-new-addpo-cb" checked> Show on PO</label>';
      html += '<div style="display:flex;gap:6px;">';
      html += '<button type="button" class="rqd-cc-cancel" data-cc-action="cancel-new" data-ref="' + esc(q.reference) + '" style="cursor:pointer;font-family:inherit;font-size:11px;">Cancel</button>';
      html += '<button type="button" class="rqd-cc-save" data-cc-action="save-new" data-ref="' + esc(q.reference) + '" style="cursor:pointer;font-family:inherit;font-size:11px;">Save</button>';
      html += '</div></div></div>';
    }
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

    // Bottom-line profit. Three modes:
    //   - Online → GP of Order (full post-cost net, always)
    //   - Offline + admin + toggle ON → GP of Order (real costs revealed)
    //   - Offline default (rep view, or admin without toggle) → GP of Goods
    //     (directTotal − COGS, identical to the historical total)
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — cardFee is REVENUE (customer-paid card
       processing surcharge), not a cost. The prior formula excluded it
       from the revenue side AND subtracted it as a cost → double-counted
       against GP by 2×cardFee. Fix: add it on the revenue side. Shopify
       processing fee stays subtracted as the real expense; cardFee +
       shopifyFees net out naturally (cardFee ~offsets shopifyFees for
       hosted-invoice / Shopify-checkout payments, leaving the small
       residual that Blue Sky actually absorbs).
       Verified against SHOP-1689 + RW-1126-A combined deal: pre-fix
       GP of Order = $2,215.09; true profit = $2,424.71; post-fix
       formula returns $2,424.71. */
    var _gpVal, _gpLabel, _gpMargin;
    if (_isFullMargin) {
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-21) — `- _palletFee` is the display half of the
         canonical formula; the arithmetic half is computeCanonicalGp() in the worker
         (lib/wm-financials-normalize.js), which charges it inside addOnProfit. THE TWO MUST MOVE
         TOGETHER — that helper's own docblock exists because this expression and the server's
         once drifted, and Marketing Ops reported a different profit than this card for three
         months. Deliberately NOT in the rep branch below: that one is GP of Goods. */
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-26) — `+ _feeDiscounts` (a NEGATIVE number) is the
         display half of the discount; the arithmetic half is computeCanonicalGp()'s `goodsRevenue`.
         Added rather than subtracted because the value already carries its sign, and because it
         belongs to REVENUE — a discount is money not collected, not money paid out, so it must not
         appear among the costs on the right of this expression. Same lockstep obligation as
         `- _palletFee` above. */
      /* CUSTOM_NOCHARGEADD_BY_CLAUDE (Ryan 2026-09-12) — `- _giveawayCost` is the display half of
         goods we put on the paid order and did not charge for; the arithmetic half is
         computeCanonicalGp(), which charges it inside addOnProfit beside the pallet fee. SAME
         LOCKSTEP OBLIGATION as `- _palletFee`: this expression and the server's are the pair that
         drifted for three months once already. Deliberately NOT in the rep branch below — that one
         is GP of Goods, and a give-away is kept out of the payout base on purpose (a rep does not
         decide when we are generous). It is not in `_gpRevenue` either: it is a cost, not revenue
         forgone, exactly like the pallet fee. */
      _gpVal = _direct + _feeDiscounts + _freightCharged + _cardFee + (q.source === 'online' ? 0 : _installCharged) - _cogs - _freightCost - _shopifyFees - _installCost - _palletFee - _giveawayCost;
      _gpLabel = 'GP of Order';
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-02) — GP of Order margin must
         divide by total ORDER revenue (goods + freight + cardFee + install),
         NOT goods alone. The old `_gpVal / _direct` was a goods-denominator
         mislabeled as order margin, and it ignored linked-balance revenue —
         SHOP-1732 showed 22.1% (off $364 parent goods) when the combined-order
         margin is ~11%. _direct + _freightCharged already include rolled-up
         linked-child revenue, so this naturally reflects both linked orders. */
      /* ⚠️ The discount belongs in the DENOMINATOR too. A discounted order took in less, so
         dividing the reduced profit by the undiscounted revenue understates the margin twice. */
      var _gpRevenue = _direct + _feeDiscounts + _freightCharged + _cardFee + (q.source === 'online' ? 0 : _installCharged);
      _gpMargin = _gpRevenue > 0 ? (_gpVal / _gpRevenue) * 100 : 0;
    } else {
      _gpVal = parseFloat(fin.gpOfGoods != null ? fin.gpOfGoods : (_direct - _cogs));
      _gpLabel = 'GP of Goods';
      _gpMargin = (fin.gpOfGoodsMargin != null) ? fin.gpOfGoodsMargin : (_direct > 0 ? (_gpVal / _direct) * 100 : 0);
    }
    html += '<div class="rqd-detail__divider"></div>';
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-25) — GP of Product reference row.
       In full-margin mode the bottom line is GP of Order (net of real freight /
       install / fees), so the goods-only product margin (Goods − COGS) isn't
       otherwise visible. Surface it as a non-emphasized reference line just above
       GP of Order so the card reads like a P&L (product margin → order net) and
       gives a quick cross-check figure for Marketing Ops. Skipped in the
       rep/default view because that view's bottom line is already GP of Goods =
       this exact number. Reuses fin.gpOfGoods / gpOfGoodsMargin (same source as
       the GP of Goods bottom line) so the two can't drift. */
    if (_isFullMargin) {
      var _gpProdVal = parseFloat(fin.gpOfGoods != null ? fin.gpOfGoods : (_direct - _cogs));
      var _gpProdMargin = (fin.gpOfGoodsMargin != null) ? fin.gpOfGoodsMargin : (_direct > 0 ? (_gpProdVal / _direct) * 100 : 0);
      html += '<div class="rqd-detail__row" title="Goods-only product margin: Goods − COGS (freight, install & fees excluded). Reference figure — distinct from the GP of Order bottom line below.">'
        + '<span>GP of Product</span><span>' + money(_gpProdVal) + ' (' + (_gpProdMargin || 0).toFixed(1) + '%)</span></div>';
    }
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    var profitClass = _gpVal > 0 ? ' rqd-detail__row--green' : _gpVal < 0 ? ' rqd-detail__row--red' : '';
    html += '<div class="rqd-detail__row' + profitClass + '"><span>' + _gpLabel + '</span><span>' + money(_gpVal) + ' (' + (_gpMargin || 0).toFixed(1) + '%)</span></div>';
    html += '</div>';
    return html;
  }

  function initDetailShell(deps) {
    deps = deps || {};
    esc = deps.esc;
    money = deps.money;
    detailRow = deps.detailRow;
    costRow = deps.costRow;
    freightCostScrollRow = deps.freightCostScrollRow;
    _qstHasContactActivity = deps._qstHasContactActivity;
    buildRequestLogisticsButtons = deps.buildRequestLogisticsButtons;
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-08) — no-op fallbacks so an out-of-order
       asset deploy degrades to "button absent" rather than a throw mid-render. */
    buildLinkExternalButton = deps.buildLinkExternalButton || function() { return ''; };
    buildLinkOpenDealButton = deps.buildLinkOpenDealButton || function() { return ''; };
    getIsAdmin = deps.getIsAdmin || function() { return false; };
    buildReviewAskMenuItem = deps.buildReviewAskMenuItem || function() { return ''; };
  }

  window.RQD.buildMobileActions = buildMobileActions;
  window.RQD.buildLinkedChildBanner = buildLinkedChildBanner;
  window.RQD.buildBalanceChainBanner = buildBalanceChainBanner;
  window.RQD.buildFinancialsCard = buildFinancialsCard;
  window.RQD.initDetailShell = initDetailShell;
})();
